import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { HelpIcon } from '@images';

import { Icon } from '@components/index';
import { FEATURE_FLAGS } from '@constants/feature';
import { AccountType, OrgStatus, UserFlow } from '@constants/generic';
import UserPermissionTypes from '@constants/permissions';
import { AUTH, HELP } from '@constants/routes';
import { STORAGE_KEYS } from '@constants/storage';
import { Event, sendMixpanelEvent } from '@core/analytics/mixpanel';
import { useCreateOrganizationMutation } from '@features/authentication/queries/AuthQuery';
import useCheckPermission from '@hooks/checkPermission';
import useNotify from '@hooks/notify';
import { OrgProfileContext } from '@layouts/constants';
import { ApiError, MapObj } from '@types';
import {
  getLocalStorageItem,
  setLocalStorageItem,
  setMultipleLocalStorageItems
} from '@utils/storage';
import { Org } from 'types/organization';

import SandboxSwitch from './SandboxSwitch';

interface ProfileListCompProps {
  orgList: Org[];
  subscriptionData: any;
  labelStyle?: string;
  accountMethods: {
    onClickLogout: () => void;
    onClickUpgradeToOrg: () => void;
    onClickSwitchAccount: () => void;
  };
  isProfileList: boolean;
}

const ProfileOptions = (props: ProfileListCompProps) => {
  const {
    orgList,
    subscriptionData,
    labelStyle,
    accountMethods,
    isProfileList
  } = props;

  const navigate = useNavigate();
  const location = useLocation();
  const { checkPermission } = useCheckPermission();
  const { createErrorAlert } = useNotify();
  const { currentUser, orgFeatureFlags } = useSelector(
    (state: any) => state.user
  );

  const orgId = getLocalStorageItem(STORAGE_KEYS.ORGANIZATION_ID);
  const isHelpActive = location.pathname === HELP;

  const currentOrg = orgList?.find((org: Org) => org.id === orgId);

  const [createOrganization] = useCreateOrganizationMutation();

  const hasIntegrationEditAccess =
    checkPermission([UserPermissionTypes.INTEGRATION_EDIT]) &&
    orgFeatureFlags &&
    orgFeatureFlags.includes(FEATURE_FLAGS.API_INTEGRATIONS);

  const onClickAddNewOrg = async () => {
    const response = await createOrganization(null);
    if ('data' in response) {
      setMultipleLocalStorageItems([
        { key: STORAGE_KEYS.NEW_ORG_ID, value: response.data?.organisation_id },
        { key: STORAGE_KEYS.NEW_BRANCH_ID, value: response.data?.branch_id },
        { key: STORAGE_KEYS.USER_FLOW, value: UserFlow.CREATE_ORG }
      ]);
      navigate(AUTH.SELECT_ACCOUNT);
    } else if ('error' in response) {
      const error = response.error as ApiError;
      createErrorAlert(error.data?.message);
    }
  };

  const onClickCreateAnAccount = () => {
    setLocalStorageItem(STORAGE_KEYS.USER_FLOW, UserFlow.USER_ONBOARD);
    navigate(AUTH.SELECT_ACCOUNT);
    sendMixpanelEvent(Event.CREATE_ACCOUNT_CLICKED);
  };

  const createNewButtonInfo: MapObj = {
    [OrgProfileContext.CREATE_ORG]: {
      label: 'Add Organization',
      onClick: onClickAddNewOrg
      // Icon: PlusIcon,
      // iconStroke: theme.grey,
      // labelStyle: 'text-darkGrey'
    },
    [OrgProfileContext.CREATE_ACCOUNT]: {
      label: 'Create an account',
      onClick: onClickCreateAnAccount
      // Icon: PlusIcon,
      // iconStroke: theme.grey,
      // labelStyle: 'text-darkGrey'
    },
    [OrgProfileContext.UPGRADE_TO_ORG]: {
      label: 'Upgrade to Organization',
      onClick: accountMethods?.onClickUpgradeToOrg,
      icon: isProfileList ? 'upgrade-to-org01' : 'upgrade-to-org'
    }
    // [OrgProfileContext.SWITCH_ACCOUNT]: {
    //   label: 'Switch Account',
    //   onClick: accountMethods?.onClickSwitchAccount,
    //   icon: 'switch-account'
    // }
  };

  const context = useMemo(() => {
    if (
      (currentOrg?.status === OrgStatus.APPROVED && !currentOrg.has_plan) ||
      currentOrg?.status === OrgStatus.VERIFICATION_PENDING ||
      currentOrg?.status === OrgStatus.VERIFICATION_SUBMITTED
    ) {
      return '';
    }

    if (
      currentUser?.is_user_onboarded &&
      currentOrg?.type === AccountType.INDIVIDUAL &&
      !currentOrg?.is_upgrade_requested &&
      (currentOrg?.status === OrgStatus.APPROVED ||
        currentOrg?.status === OrgStatus.KYC_PENDING) &&
      subscriptionData?.status !== 'expired'
    )
      return OrgProfileContext.UPGRADE_TO_ORG;
    // Add an Org flow commented out for first phase
    // if (
    //   currentOrg?.role === OrgRoleType.OWNER &&
    //   currentOrg?.type === AccountType.ORGANIZATION
    // )
    //   return OrgProfileContext.CREATE_ORG;
    if (!currentUser?.is_user_onboarded)
      return OrgProfileContext.CREATE_ACCOUNT;
    return '';
  }, [currentUser, currentOrg, subscriptionData]);

  const renderProfileSettingsItem = (ctx: OrgProfileContext) => {
    const { label, onClick, icon } = createNewButtonInfo[ctx];
    return (
      <div
        className={`flex cursor-pointer items-center rounded-md ${
          isProfileList
            ? 'ml-2 mr-1 gap-3 py-2.5 pl-3 pr-2 hover:bg-cofeeGreyBlue75'
            : 'mx-1 gap-3 px-4 py-2.5 hover:bg-cofeeGreyBlue75'
        }`}
        role="presentation"
        onClick={onClick}
      >
        {icon && (
          <Icon
            name={icon}
            size="none"
            className={`size-5 ${
              isProfileList ? 'text-cofeeNeutral8' : 'text-cofeeNeutral10'
            }`}
          />
        )}
        <div className={`text-sm ${labelStyle}`}>{label}</div>
      </div>
    );
  };

  const profileItemClick = (label: string) => {
    sendMixpanelEvent(Event.SIDEBAR_TAB_CLICKED, {
      section: label
    });
  };

  return (
    <div>
      {hasIntegrationEditAccess && (
        <SandboxSwitch isProfileList={isProfileList} />
      )}
      {/* Add an Organization / Create an Account / Switch to Organization */}
      <div className="mb-1 h-px w-full bg-cofeeNeutral5" />
      {context && renderProfileSettingsItem(context)}
      {/* {orgList &&
        isProfileList &&
        orgList.length > 1 &&
        renderProfileSettingsItem(OrgProfileContext.SWITCH_ACCOUNT)} */}
      {isProfileList && <div className="mb-1 h-px w-full" />}
      <div className="relative mr-1">
        <Link
          key="help"
          to={HELP}
          className={`mx-1 flex items-center gap-[12px] rounded-md px-4 py-2.5 text-sm text-cofeeNeutral10 ${
            isHelpActive && isProfileList
              ? 'bg-cofeeGreyBlue75 hover:bg-cofeeGreyBlue75'
              : 'hover:bg-cofeeGreyBlue75'
          }`}
          onClick={() => profileItemClick('Help')}
        >
          <HelpIcon
            className={`size-5 ${
              isProfileList
                ? `${
                    !isHelpActive ? 'text-cofeeNeutral8' : 'text-cofeePrimary6'
                  }`
                : 'text-cofeeNeutral10'
            }`}
          />
          Help
        </Link>
        {isHelpActive && isProfileList && (
          <div
            className="absolute -right-1 top-0 h-10 w-1 
               rounded-l-lg bg-cofeePrimary6"
          />
        )}
      </div>
      <div
        role="presentation"
        className={`flex cursor-pointer items-center rounded-md py-2 text-sm font-normal hover:bg-cofeeGreyBlue75 ${
          isProfileList
            ? 'ml-2 mr-1 gap-3 py-2.5 pl-3 pr-2 hover:bg-cofeeGreyBlue75'
            : 'mx-1 gap-3 px-4 py-2.5 hover:bg-cofeeGreyBlue75'
        } ${labelStyle}`}
        onClick={accountMethods?.onClickLogout}
      >
        <Icon
          name="logout-icon"
          size="none"
          className={`size-4.5 ${
            isProfileList ? 'text-cofeeNeutral8' : 'text-cofeeNeutral10'
          }`}
        />
        Log out
      </div>
    </div>
  );
};

export default ProfileOptions;
